/**
 * Gravação de teleconsulta no browser (Plano B): captura áudio da aba Jitsi via getDisplayMedia.
 */

import {
  completeBrowserRecording,
  presignBrowserRecording,
  uploadBlobToPresignedUrl,
} from '../../services/teleconsultService';

export type BrowserRecordingPhase =
  | 'idle'
  | 'awaiting_share'
  | 'recording'
  | 'uploading'
  | 'done'
  | 'failed';

export function pickAudioMimeType(): string {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  for (const t of types) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) return t;
  }
  return 'audio/webm';
}

/** Pede compartilhamento da aba com áudio; devolve só faixas de áudio. */
export async function captureTabAudioStream(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error('Seu navegador não suporta gravação de áudio da aba.');
  }

  // Flags Chromium (preferCurrentTab, systemAudio) ainda não estão em DisplayMediaStreamOptions do TS DOM.
  // Chrome não aceita preferCurrentTab + selfBrowserSurface:'exclude' (config autocontraditória).
  const displayOpts = {
    video: true,
    audio: true,
    preferCurrentTab: true,
    systemAudio: 'exclude',
  } as DisplayMediaStreamOptions;

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia(displayOpts);
  } catch (firstErr: any) {
    const msg = typeof firstErr?.message === 'string' ? firstErr.message : '';
    if (msg.includes('Self-contradictory') || msg.includes('constraints')) {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });
    } else {
      throw firstErr;
    }
  }
  const audioTracks = stream.getAudioTracks();
  if (!audioTracks.length) {
    stream.getTracks().forEach((t) => t.stop());
    throw new Error(
      'Marque "Compartilhar áudio da aba" ao escolher esta página. Sem áudio não há gravação.',
    );
  }

  stream.getVideoTracks().forEach((t) => t.stop());
  return new MediaStream(audioTracks);
}

const STOP_RECORDING_TIMEOUT_MS = 20_000;

export class BrowserTeleconsultRecorder {
  private stream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  /** Disparado quando o usuário encerra o compartilhamento da aba no Chrome. */
  onCaptureEnded: (() => void) | null = null;

  isRecording(): boolean {
    const s = this.mediaRecorder?.state;
    return s === 'recording' || s === 'paused';
  }

  hasCapturedData(): boolean {
    return this.chunks.length > 0;
  }

  async start(): Promise<void> {
    if (this.isRecording()) return;
    this.chunks = [];
    this.stream = await captureTabAudioStream();
    const mimeType = pickAudioMimeType();
    this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
    this.mediaRecorder.ondataavailable = (ev) => {
      if (ev.data?.size) this.chunks.push(ev.data);
    };
    this.stream.getAudioTracks().forEach((track) => {
      track.addEventListener('ended', () => {
        this.onCaptureEnded?.();
      });
    });
    this.mediaRecorder.start(1000);
  }

  stopTracks(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  private buildBlobFromChunks(mimeHint?: string): Blob {
    const mime = mimeHint || this.mediaRecorder?.mimeType || pickAudioMimeType();
    return new Blob(this.chunks, { type: mime });
  }

  private async finalizeBlob(): Promise<Blob> {
    const recorder = this.mediaRecorder;
    if (!recorder) {
      if (this.chunks.length === 0) {
        throw new Error('Nenhuma gravação em andamento.');
      }
      const b = this.buildBlobFromChunks();
      this.chunks = [];
      this.stopTracks();
      if (!b.size) {
        throw new Error('Gravação vazia. Compartilhe o áudio da aba e tente de novo.');
      }
      return b;
    }

    if (recorder.state === 'inactive') {
      const b = this.buildBlobFromChunks();
      this.chunks = [];
      this.mediaRecorder = null;
      this.stopTracks();
      if (!b.size) {
        throw new Error('Gravação vazia. Compartilhe o áudio da aba e tente de novo.');
      }
      return b;
    }

    const blob = await new Promise<Blob>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(
          new Error(
            'Tempo esgotado ao finalizar a gravação. Tente gravar de novo e não feche o compartilhamento da aba.',
          ),
        );
      }, STOP_RECORDING_TIMEOUT_MS);

      recorder.onstop = () => {
        window.clearTimeout(timeout);
        const b = this.buildBlobFromChunks(recorder.mimeType);
        this.chunks = [];
        this.mediaRecorder = null;
        this.stopTracks();
        if (!b.size) {
          reject(new Error('Gravação vazia. Compartilhe o áudio da aba e tente de novo.'));
          return;
        }
        resolve(b);
      };
      recorder.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error('Erro ao finalizar gravação.'));
      };
      try {
        if (recorder.state === 'recording' && typeof recorder.requestData === 'function') {
          recorder.requestData();
        }
        recorder.stop();
      } catch (e) {
        window.clearTimeout(timeout);
        reject(e);
      }
    });

    return blob;
  }

  async stopAndUpload(appointmentId: string): Promise<void> {
    if (!appointmentId?.trim()) {
      throw new Error('Agendamento inválido. Recarregue a página e entre na consulta de novo.');
    }

    const blob = await this.finalizeBlob();

    const ext = blob.type.includes('mp4') ? 'm4a' : 'webm';
    const fileName = `browser-${Date.now()}.${ext}`;
    const contentType = blob.type || pickAudioMimeType();

    const presign = await presignBrowserRecording(appointmentId, fileName, contentType);
    await uploadBlobToPresignedUrl(presign.uploadUrl, blob, presign.contentType);
    await completeBrowserRecording(appointmentId, presign.objectKey);
  }

  dispose(): void {
    try {
      if (this.mediaRecorder?.state === 'recording') {
        this.mediaRecorder.stop();
      }
    } catch {
      // ignore
    }
    this.mediaRecorder = null;
    this.chunks = [];
    this.stopTracks();
  }
}
